import { apiServerUrl, ExternalApi } from "@/apis/apiService";
import { usePaymentsStore, useEMandatesStore } from "@/plugins/store";
import { keysToCamelCase } from "@/helpers/stringProcessors";
import {
    Organisation,
    Form,
    PaymentMethod,
} from "@/helpers/interfaces/organisations";
import { redirectToRoute } from "@/plugins/router";
import { routeNames } from "@/constants/routeNames";
import { isActiveNow } from "@/helpers/dateProcessors";
import { getRequestConfig } from "@esenda/core-ui/utils";
import { getCommonHeaderParams } from "@/helpers/api";
import { HTTP_METHOD } from "@esenda/core-ui/constants";
import { getUserLocaleForAdyen } from "@/helpers/session";
import { useOrganisationStore } from "@/plugins/store";

export const getOrganisationData = async (
    shortcode: string,
    formName: string,
) => {
    /**
     * Internal API call to retrieve the organisation data. If successful,
     * it will save the data to the session storage, otherwise it will redirect
     * to 404 or display an error.
     * @param  {[string]} shortcode - organisation shortcode
     * @param  {[string]} formName - invoice/others
     * @return {[{void}]}
     */
    const orgStore = useOrganisationStore();

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to refresh the page
        sentryTransactionName: "getOrganisationDataAPI",
        redirectTo: "404", // redirect to the 404 page
    };

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}?form-name=${formName}`,
            HTTP_METHOD.GET,
            getCommonHeaderParams(),
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const organisation: Organisation = keysToCamelCase(data);
        orgStore.setShortcode(shortcode);
        orgStore.setOrganisation(organisation);
    }
};

export const getFormData = async (shortcode: string, formName: string) => {
    /**
     * Internal API call to retrieve the form structure. If successful,
     * it will save the data to the session storage, otherwise it will redirect
     * to 404 or display an error.
     * If the link is disabled or inactive it will redirect the user to the
     * following route /payment-link-unavailable.
     * @param  {[string]} shortcode - organisation shortcode
     * @param  {[string]} formName - invoice/others
     * @return {[{void}]}
     */
    const pStore = useEMandatesStore();
    const orgStore = useOrganisationStore();

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to refresh the page
        sentryTransactionName: "getFormDataAPI",
        redirectTo: "404", // redirect to the 404 page
    };

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/forms?form-name=${formName}`,
            HTTP_METHOD.GET,
            getCommonHeaderParams(),
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const formStructure: Form = keysToCamelCase(data);
        orgStore.setFormStructure(formStructure);
        orgStore.setFormName(formStructure.formName);

        // If form is not enabled, redirect to /payment-link-unavailable
        // Forms without the isEnabled value will be treated as enabled
        if ("isEnabled" in formStructure && !formStructure.isEnabled) {
            redirectToRoute(routeNames.PAYMENT_LINK_UNAVAILABLE, {
                shortcode: orgStore.shortcode,
                accountAlias: pStore.accountAlias,
            });
        }

        // If the current date is not within the form's activeTo - activeFrom dates,
        // redirect to /payment-link-unavailable.
        // Forms without both activeTo&activeFrom values will be treated as active
        const isFormWithinDates = isActiveNow(
            formStructure.activeFrom,
            formStructure.activeTo,
        );
        if (!isFormWithinDates) {
            redirectToRoute(routeNames.PAYMENT_LINK_UNAVAILABLE, {
                shortcode: orgStore.shortcode,
                accountAlias: pStore.accountAlias,
            });
        }

        // If form is has reached the max limit, redirect to /payment-link-unavailable
        // Forms without the maxPaymentsReached value will be treated as enabled
        if (
            "maxPaymentsReached" in formStructure &&
            formStructure.maxPaymentsReached
        ) {
            redirectToRoute(routeNames.PAYMENT_LINK_UNAVAILABLE, {
                shortcode: orgStore.shortcode,
                accountAlias: pStore.accountAlias,
            });
        }
    }
};

export const getPaymentMethodsList = async (
    countryCode: string,
    amount: number,
) => {
    /**
     * Internal API call to retrieve the the payment methods list. If successful,
     * it will save the data to the session storage and redirect the user to the payment
     * page, otherwise it will display an error.
     * @param  {[string]} countryCode - two letter country code
     * @param  {[Number]} amount - payment amount
     * @return {[{void}]}
     */
    const paymentStore = usePaymentsStore();
    const orgStore = useOrganisationStore();
    const formNameParam = orgStore.formName
        ? `&form-name=${orgStore.formName}`
        : "";

    /** Clear the current selected provider */
    paymentStore.setSelectedProvider(null);
    paymentStore.setSelectedProviderDetails(null);

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "getPaymentMethodsListAPI",
    };

    const locale = getUserLocaleForAdyen();

    const urlParams = `?country=${countryCode}&amount=${amount}${formNameParam}&locale=${locale}`;

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${orgStore.shortcode}/payment-methods${urlParams}`,
            HTTP_METHOD.GET,
            getCommonHeaderParams(),
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const paymentMethods: PaymentMethod[] = keysToCamelCase(data);
        paymentStore.setPaymentMethods(paymentMethods);
        return paymentMethods;
    } else {
        return null;
    }
};
